import React, { useState, useEffect } from 'react';
import { ChevronRight } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Hero = ({ onJoinWaitlist }) => {
  const [apyRate, setApyRate] = useState('3.75');
  const [totalWaiting, setTotalWaiting] = useState(1247);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [apyResponse, statsResponse] = await Promise.all([
          axios.get(`${API}/settings/apy`),
          axios.get(`${API}/waitlist/stats`)
        ]);
        setApyRate(apyResponse.data.apy_rate.toFixed(2));
        setTotalWaiting(statsResponse.data.total_users);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const refCode = urlParams.get('ref');

      const response = await axios.post(`${API}/waitlist/join`, {
        email,
        referral_code: refCode
      });

      if (response.data.success === false) {
        toast({
          title: "Welcome back!",
          description: `You're already on the list at position #${response.data.existing_data.position}`,
        });
      } else {
        localStorage.setItem('saverr_waitlist', JSON.stringify({
          position: response.data.position,
          referralLink: response.data.referral_link,
          referralCode: response.data.referral_code
        }));

        toast({
          title: "You're on the list!",
          description: `You're #${response.data.position} in line. Check your email for your referral link!`,
        });

        setEmail('');
        
        // Scroll to waitlist section to show success
        setTimeout(() => {
          onJoinWaitlist();
        }, 1500);
      }
    } catch (error) {
      console.error('Error joining waitlist:', error);
      toast({
        title: "Error",
        description: "Failed to join waitlist. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden" style={{
      backgroundColor: '#fafafa',
      backgroundImage: `
        radial-gradient(at 0% 0%, hsla(217,91%,93%,1) 0px, transparent 50%),
        radial-gradient(at 100% 0%, hsla(189,100%,94%,1) 0px, transparent 50%),
        radial-gradient(at 100% 100%, hsla(245,86%,94%,1) 0px, transparent 50%),
        radial-gradient(at 0% 100%, hsla(217,91%,93%,1) 0px, transparent 50%)
      `
    }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center">
          {/* Left Column */}
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-100 mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
              </span>
              <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Optimization Engine Live</span>
            </div>
            
            <h1 className="text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.1] mb-6">
              Savings that work <br className="hidden lg:block" />
              <span className="bg-gradient-to-r from-blue-900 to-blue-600 bg-clip-text text-transparent">harder for you</span>
            </h1>
            
            <p className="text-xl text-slate-600 mb-8 leading-relaxed max-w-lg">
              Stop settling for low rates. Saverr automatically moves your money to top-tier, FDIC-insured accounts yielding up to 10x the national average.
            </p>

            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 mb-10 max-w-lg">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                required
                className="flex-1 px-5 py-3.5 rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm text-slate-900 placeholder-slate-400"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-8 py-3.5 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Joining...' : 'Join the Waitlist'}
              </button>
            </form>

            <div className="flex items-center gap-4">
              <div className="flex -space-x-3">
                <img src="https://randomuser.me/api/portraits/women/44.jpg" alt="User" className="w-10 h-10 rounded-full border-2 border-white object-cover" />
                <img src="https://randomuser.me/api/portraits/men/32.jpg" alt="User" className="w-10 h-10 rounded-full border-2 border-white object-cover" />
                <img src="https://randomuser.me/api/portraits/women/68.jpg" alt="User" className="w-10 h-10 rounded-full border-2 border-white object-cover" />
                <div className="w-10 h-10 rounded-full border-2 border-white bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600">
                  +1k
                </div>
              </div>
              <div className="flex flex-col">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <span className="text-sm font-medium text-slate-600">
                  <span className="font-bold text-slate-900">{totalWaiting.toLocaleString()}+</span> people waiting
                </span>
              </div>
            </div>
          </div>

          {/* Right Column - Dashboard */}
          <div className="relative">
            <div className="absolute -top-20 -right-20 w-96 h-96 bg-blue-400/20 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-purple-400/20 rounded-full blur-3xl"></div>

            <div className="relative bg-white rounded-3xl shadow-2xl shadow-blue-900/10 p-6 border border-slate-100 backdrop-blur-sm z-10 transform transition-transform hover:scale-[1.02] duration-500">
              <div className="mb-8">
                <p className="text-sm text-slate-500 font-medium mb-1">Total Balance</p>
                <div className="flex items-center gap-3">
                  <h3 className="text-3xl font-bold text-slate-900">$24,562.00</h3>
                  <div className="bg-green-50 px-3 py-1 rounded-full border border-green-100 flex items-center gap-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                    <span className="text-xs font-bold text-green-700">+{apyRate}%</span>
                  </div>
                </div>
              </div>

              <div className="h-48 w-full mb-6 relative">
                <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" style={{stopColor:'#3b82f6', stopOpacity:0.2}} />
                      <stop offset="100%" style={{stopColor:'#3b82f6', stopOpacity:0}} />
                    </linearGradient>
                  </defs>
                  <path d="M0,150 C50,140 100,130 150,100 C200,70 250,90 300,50 C350,10 400,30 450,20 C500,10 550,5 600,0" fill="url(#gradient)" stroke="none" />
                  <path d="M0,150 C50,140 100,130 150,100 C200,70 250,90 300,50 C350,10 400,30 450,20 C500,10 550,5 600,0" fill="none" stroke="#3b82f6" strokeWidth="3" strokeLinecap="round" />
                </svg>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex-1 bg-slate-50 rounded-xl p-3 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white border border-slate-100 flex items-center justify-center shadow-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 font-medium">Interest Earned</p>
                    <p className="text-sm font-bold text-slate-900">+$245.80</p>
                  </div>
                </div>
                <div className="flex-1 bg-slate-50 rounded-xl p-3 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white border border-slate-100 flex items-center justify-center shadow-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 font-medium">Optimization</p>
                    <p className="text-sm font-bold text-slate-900">Active</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Card - Current Yield */}
            <div className="absolute -right-6 top-10 bg-white p-4 rounded-2xl shadow-xl shadow-blue-500/10 border border-slate-100 z-20" style={{animation: 'bounce 3s infinite'}}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span className="text-sm font-bold text-slate-900">Current Yield</span>
              </div>
              <p className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{apyRate}%</p>
              <p className="text-xs text-slate-500 mt-1">APY • FDIC Insured</p>
            </div>

            {/* Comparison Card */}
            <div className="absolute -left-12 bottom-20 bg-slate-900 p-4 rounded-2xl shadow-2xl z-20 hidden md:block">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-full bg-slate-800 flex items-center justify-center border border-slate-700">
                  <span className="text-xs text-white font-bold">VS</span>
                </div>
                <span className="text-sm font-medium text-slate-200">Vs National Avg</span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div className="w-[12%] h-full bg-slate-600"></div>
                  </div>
                  <span className="text-xs text-slate-400">0.46%</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div className="w-full h-full bg-blue-500"></div>
                  </div>
                  <span className="text-xs text-blue-400 font-bold">{apyRate}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
